/**
 * Shown to non-host players during a game.
 * Compact panel: their current score + top 3 leaderboard.
 * Designed to NOT compete with the buzzer button for attention —
 * sits in a corner card, glanceable but quiet.
 */
const PlayerStats = ({ scores, participants, myName, theme }) => {
  // Exclude host from rankings
  const hostNames = new Set(participants.filter((p) => p.is_host).map((p) => p.name));
  const rows = Object.entries(scores || {})
    .filter(([name]) => !hostNames.has(name))
    .map(([name, score]) => ({ name, score }))
    .sort((a, b) => b.score - a.score);

  const myScore = scores?.[myName] || 0;
  const myRank = rows.findIndex((r) => r.name === myName) + 1; // 1-indexed
  const top3 = rows.slice(0, 3);

  const accent = theme?.accentColor || "#00F0FF";
  const title = theme?.titleColor || "#FFEA00";

  const medal = (idx) =>
    idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `${idx + 1}.`;

  return (
    <div
      className="w-full max-w-xs mx-auto mb-6 bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden"
      data-testid="player-stats"
    >
      {/* "Your score" header */}
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <p className="text-[10px] text-[#A1A1AA] tracking-widest font-semibold uppercase">
            Your score
          </p>
          <p
            className="text-3xl font-bold tabular-nums leading-none mt-0.5"
            style={{ fontFamily: "Anton, sans-serif", color: accent }}
            data-testid="my-score"
          >
            {myScore}
          </p>
        </div>
        {myRank > 0 && (
          <div className="text-right">
            <p className="text-[10px] text-[#A1A1AA] tracking-widest font-semibold uppercase">
              Rank
            </p>
            <p
              className="text-2xl font-bold leading-none mt-0.5"
              style={{ fontFamily: "Anton, sans-serif", color: title }}
              data-testid="my-rank"
            >
              {myRank === 1 ? "🥇" : myRank === 2 ? "🥈" : myRank === 3 ? "🥉" : `#${myRank}`}
            </p>
          </div>
        )}
      </div>

      {/* Top 3 list */}
      {top3.length > 0 && (
        <div>
          <p className="px-4 pt-2 text-[10px] text-[#A1A1AA] tracking-widest font-semibold uppercase">
            Top 3
          </p>
          <ul className="px-4 pb-3 pt-1 space-y-1">
            {top3.map((r, idx) => {
              const isMe = r.name === myName;
              return (
                <li
                  key={r.name}
                  className={`flex items-center gap-2 py-1 ${isMe ? "" : ""}`}
                  data-testid={`top3-row-${idx + 1}`}
                >
                  <span className="text-sm w-6">{medal(idx)}</span>
                  <span
                    className={`flex-1 truncate text-sm ${
                      isMe ? "text-white font-bold" : "text-[#D4D4D8]"
                    }`}
                  >
                    {r.name}
                    {isMe && (
                      <span className="ml-1 text-[10px] uppercase tracking-wide opacity-70">
                        (you)
                      </span>
                    )}
                  </span>
                  <span
                    className="text-base font-bold tabular-nums"
                    style={{ fontFamily: "Anton, sans-serif", color: accent }}
                  >
                    {r.score}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PlayerStats;
